import { defineCollection } from "@rebasepro/cms-types";
import playersCollection from "./players";
import challengesCollection from "./challenges";
import type { CollectionConfig } from "@rebasepro/types";
import { moment } from "../words";

// A challenge's board (songbuzz_solo_duel_score), one row per challenge and device. backend/functions/songbuzz.ts
// creates the table and writes it; admins can rename a player or take a score off the board.
const challengeScoresCollection = defineCollection({
    name: "Challenge scores",
    singularName: "Challenge score",
    slug: "challenge_scores",
    table: "songbuzz_solo_duel_score",
    description: "Each challenge's leaderboard: one score per player.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operations: ["update", "delete"], roles: ["admin"] },
    ],
    properties: {
        duel: { name: "Challenge code", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        // the account ("u:<uid>") or, for Solo without one, the device
        sid: { name: "Player id", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        player: {
            name: "Player", type: "relation",
            relation: { kind: "via", cardinality: "one", target: (): CollectionConfig => playersCollection, relationName: "player", joinPath: [{ table: "songbuzz_players", on: { from: "sid", to: "id" } }] },
            admin: { readOnly: true },
        },
        challenge: {
            name: "Challenge", type: "relation",
            relation: { kind: "via", cardinality: "one", target: (): CollectionConfig => challengesCollection, relationName: "challenge", joinPath: [{ table: "songbuzz_solo_duel", on: { from: "duel", to: "id" } }] },
            admin: { readOnly: true },
        },
        name: { name: "Name on the board", type: "string" },
        hype: { name: "Score (hype)", type: "number", columnType: "bigint", validation: { required: true }, admin: { readOnly: true } },
        songs: { name: "Songs played", type: "number", validation: { integer: true }, admin: { readOnly: true } },
        rights: { name: "Songs right", type: "number", validation: { integer: true }, admin: { readOnly: true } },
        // a short hash of the look they wore, so the board draws them as they were
        look: { name: "Look code", type: "string", admin: { readOnly: true, hideFromCollection: true } },
        cum: {
            name: "Hype after each song", type: "array", columnType: "jsonb", of: { name: "Hype", type: "number" },
            admin: { readOnly: true, hideFromCollection: true, span: 4, Preview: "../../admin/src/previews/HypeCurve" },
        },
        at: { name: "When", type: "date", autoValue: "on_create", validation: { required: true }, admin: { readOnly: true } },
    },
    admin: {
        icon: "Medal",
        group: "Challenges",
        openEntityMode: "side_panel",
        sort: ["hype", "desc"],
        display: {
            title: ({ entity }) => entity.values.name || "No name",
            // whose challenge it was: the code alone says nothing
            subtitle: async ({ entity, context }) => {
                const c = await context.client.data.collection<{ byName?: string | null }>("challenges").findById(String(entity.values.duel ?? "")).catch(() => undefined);
                return [c?.byName ? `${c.byName}'s challenge` : `Challenge ${entity.values.duel}`, moment(entity.values.at)].join(" · ");
            },
            date: "at",
        },
        listProperties: ["hype", "rights", "songs"],
        form: {
            sections: [
                { key: "score", properties: ["player", "challenge", "name", "hype", "rights", "songs", "cum", "at"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["duel", "sid", "look"] },
            ],
        },
    },
});

export default challengeScoresCollection;
